import {
  ArrowLeftIcon,
  ArrowRightIcon,
  DatabaseIcon,
  WarningIcon
} from "@phosphor-icons/react";
import type { ReactNode } from "react";

export function PageHeader({
  title,
  description,
  code,
  actions
}: {
  title: string;
  description?: string;
  code?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        {code && <samp className="section-code">{code}</samp>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export function StatusTag({ tone = "neutral", children }: { tone?: "ok" | "danger" | "warning" | "neutral"; children: ReactNode }) {
  return <span className={`status-tag ${tone}`}>{children}</span>;
}

export function EmptyState({ title, text, action }: { title: string; text: string; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <DatabaseIcon size={28} weight="thin" aria-hidden="true" />
      <strong>{title}</strong>
      <span>{text}</span>
      {action}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="inline-error" role="alert">
      <WarningIcon size={21} aria-hidden="true" />
      <div><strong>Данные не загрузились</strong><span>{message}</span></div>
      {onRetry && <button className="button secondary compact" onClick={onRetry}>Повторить</button>}
    </div>
  );
}

export function SkeletonRows({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="skeleton-table" aria-label="Загрузка данных" aria-busy="true">
      {Array.from({ length: rows }, (_, row) => (
        <div className="skeleton-row" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }} key={row}>
          {Array.from({ length: columns }, (_, column) => <i key={column} />)}
        </div>
      ))}
    </div>
  );
}

export function Pagination({ page, pages, onChange }: { page: number; pages: number; onChange: (page: number) => void }) {
  if (pages <= 1 && page <= 1) return null;
  return (
    <nav className="pagination" aria-label="Страницы">
      <button className="icon-button" disabled={page <= 1} onClick={() => onChange(page - 1)} aria-label="Предыдущая страница">
        <ArrowLeftIcon size={17} />
      </button>
      <samp>{page.toLocaleString("ru-RU")} / {Math.max(1, pages).toLocaleString("ru-RU")}</samp>
      <button className="icon-button" disabled={page >= pages} onClick={() => onChange(page + 1)} aria-label="Следующая страница">
        <ArrowRightIcon size={17} />
      </button>
    </nav>
  );
}

export function BackButton({ onClick, children = "Назад" }: { onClick: () => void; children?: ReactNode }) {
  return <button className="back-button" onClick={onClick}><ArrowLeftIcon size={16} />{children}</button>;
}
